import {
  Box,
  Card,
  CardBody,
  Flex,
  HStack,
  Text,
  Badge,
  Button,
  Icon,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faLightbulb,
  faCircleInfo,
  faArrowRight,
  faCheck,
  faXmark,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';

const SEVERITY = {
  critical: { color: 'red', icon: faTriangleExclamation, label: 'Action needed', hex: '#ef4444' },
  opportunity: { color: 'brand', icon: faLightbulb, label: 'Opportunity', hex: '#6366f1' },
  info: { color: 'gray', icon: faCircleInfo, label: 'For info', hex: '#94a3b8' },
};

const HIGH_IMPACT = 70;

function timeAgo(date) {
  if (!date) return '';
  const secs = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export const InsightCard = ({ insight, onAcknowledge, onDismiss, busy }) => {
  const navigate = useNavigate();
  const sev = SEVERITY[insight.severity] || SEVERITY.info;
  const isAck = insight.status === 'acknowledged';
  const isHighImpact = (insight.data?.impact || 0) >= HIGH_IMPACT;
  const metrics = insight.data?.metrics || [];
  const spark = insight.data?.spark || [];

  // Soft severity-tinted panel: subtle background + matching border.
  // 'info' uses the gray scale (== page bg), so render it as a neutral card instead.
  const tinted = insight.severity !== 'info';
  const cardBg = useColorModeValue(tinted ? `${sev.color}.50` : 'white', tinted ? `${sev.color}.900` : 'gray.800');
  const borderColor = useColorModeValue(tinted ? `${sev.color}.100` : 'gray.100', tinted ? `${sev.color}.800` : 'gray.700');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const metaColor = useColorModeValue('gray.500', 'gray.500');
  const chipBg = useColorModeValue('whiteAlpha.700', 'blackAlpha.300');
  const tileBg = useColorModeValue('white', 'whiteAlpha.200');

  return (
    <Card
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      position="relative"
      overflow="hidden"
      h="100%"
      opacity={isAck ? 0.65 : 1}
      transition="all 0.2s"
      _hover={{ borderColor: `${sev.color}.300`, transform: 'translateY(-1px)', boxShadow: 'sm' }}
    >
      <CardBody>
        <Flex gap={4} align="flex-start">
          <Flex
            align="center"
            justify="center"
            w="40px"
            h="40px"
            borderRadius="xl"
            bg={tileBg}
            flexShrink={0}
          >
            <Icon color={`${sev.color}.500`} boxSize={4}>
              <FontAwesomeIcon icon={sev.icon} />
            </Icon>
          </Flex>

          <Box flex={1} minW={0}>
            <Flex justify="space-between" align="flex-start" gap={3} mb={1} wrap="wrap">
              <Text fontWeight="600" fontSize="md">{insight.title}</Text>
              <HStack spacing={2} flexShrink={0}>
                {isHighImpact && (
                  <Badge colorScheme="orange" variant="subtle" borderRadius="full" px={2}>
                    <HStack spacing={1}>
                      <FontAwesomeIcon icon={faBolt} size="xs" />
                      <Text>High impact</Text>
                    </HStack>
                  </Badge>
                )}
                {isAck && (
                  <Badge colorScheme="green" variant="subtle" borderRadius="full" px={2}>
                    Acknowledged
                  </Badge>
                )}
              </HStack>
            </Flex>

            <Text fontSize="sm" color={textSecondary} mb={3}>{insight.body}</Text>

            {(metrics.length > 0 || spark.length > 1) && (
              <Flex align="center" justify="space-between" gap={3} mb={3} wrap="wrap">
                <HStack spacing={2} wrap="wrap" flex={1} minW={0}>
                  {metrics.map((m, i) => (
                    <Box key={i} bg={chipBg} borderRadius="lg" px={2.5} py={1}>
                      <Text fontSize="10px" color={metaColor} textTransform="uppercase" letterSpacing="0.04em" lineHeight="1.2">
                        {m.label}
                      </Text>
                      <Text fontSize="sm" fontWeight="700" lineHeight="1.3">{m.value}</Text>
                    </Box>
                  ))}
                </HStack>
                {spark.length > 1 && (
                  <Box w="84px" h="32px" flexShrink={0}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={spark.map((v) => ({ v }))} margin={{ top: 4, bottom: 4, left: 0, right: 0 }}>
                        <Line type="monotone" dataKey="v" stroke={sev.hex} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Flex>
            )}

            <Flex justify="space-between" align="center" gap={2} wrap="wrap">
              <Text fontSize="xs" color={metaColor}>
                {sev.label} · {timeAgo(insight.last_seen)}
              </Text>
              <HStack spacing={2}>
                {insight.deep_link && (
                  <Button
                    size="xs"
                    variant="solid"
                    colorScheme="brand"
                    borderRadius="lg"
                    rightIcon={<FontAwesomeIcon icon={faArrowRight} />}
                    onClick={() => navigate(insight.deep_link)}
                  >
                    Open
                  </Button>
                )}
                {!isAck && (
                  <Tooltip label="Mark as seen" placement="top" hasArrow>
                    <Button
                      size="xs"
                      variant="ghost"
                      borderRadius="lg"
                      isLoading={busy}
                      leftIcon={<FontAwesomeIcon icon={faCheck} />}
                      onClick={() => onAcknowledge(insight.insight_id)}
                    >
                      Acknowledge
                    </Button>
                  </Tooltip>
                )}
                <Tooltip label="Dismiss" placement="top" hasArrow>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                    borderRadius="lg"
                    isLoading={busy}
                    aria-label="Dismiss"
                    onClick={() => onDismiss(insight.insight_id)}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </Button>
                </Tooltip>
              </HStack>
            </Flex>
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};

export default InsightCard;
